import { Button, Chip } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import FuelTable from '@/components/fuel-table/FuelTable';
import { useProgress } from '@/contexts/ProgressProvider';

import FuelActionbar from '../../components/fuel-actionbar';
import FuelAssetThumbnail from '../../components/FuelAssetThumbnail/FuelAssetThumbnail';
import { Permission } from '../../enums';
import type { Item } from '../../models';
import type { Replay } from '../../models/Replay';
import { enhanceFetchedAssets } from '../../pages/assets/asset-listing/enhanceFetchedAsset';
import { FuelPortalService } from '../../services';
import { applicationDataSelector } from '../../store/slices/application-data';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import useAppUtils from '../../utils/appUtils';
import { dateUtils } from '../../utils/dateUtils';
import { useModalContext } from '../VNModalProvider';
import VnModalBody from './VnModalBody';
import VnModalFooter from './VnModalFooter';

const initialPagination = {
  sortBy: 'publicationDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

const columnHelper = createColumnHelper<Item>();
const SegmentsInReplayModal = ({ serviceId }) => {
  //   const [replay, setReplay] = useState<Replay>(null);
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const { vnConfirm, alert } = useAppUtils();
  const [clips, setClips] = useState<Item[]>([]);
  const replayRef = useRef(null);
  const [pagination, setPagination] = useState(initialPagination);
  const { loading, setProgress } = useProgress();
  const { onClose } = useModalContext();

  const Thumbnail = useCallback(
    (cell) => <FuelAssetThumbnail item={cell.row.original} permissions={permissions} />,
    [permissions]
  );

  const columns = [
    columnHelper.accessor('thumbnailSrc', {
      header: '',
      cell: Thumbnail,
    }),
    columnHelper.accessor('title', {
      align: 'left',
      header: () => 'Name',
      width: 500,
      sortable: true,
      cell: (cell) => {
        return (
          <div
            title={cell.getValue()}
            // onClick={(event) => onTableAction(event, 'goToAssociatedChannel', cell.row.original, cell.table, cell.row.index)}
          >
            <span>{cell.getValue()}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('publicationDate', {
      header: () => `Publication Date (${ouData.timezoneAbbreviation})`,
      width: 150,
      sortable: true,
      cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    }),
    columnHelper.accessor('segmentStatusInReplay', {
      header: () => `Status`,
      width: 150,
      sortable: true,
      cell: (cell) => {
        return (
          <div
            title={cell.getValue()}
            // onClick={(event) => onTableAction(event, 'goToAssociatedChannel', cell.row.original, cell.table, cell.row.index)}
          >
            {/* <span>{cell.getValue()}</span> */}
            {cell.row.original.segmentStatusInReplay && (
              <Chip
                title={cell.row.original.segmentStatusInReplay}
                label={cell.row.original.segmentStatusInReplay}
                size="small"
                variant="outlined"
                color={cell.row.original.segmentStatusInReplay === 'Live' ? 'success' : 'default'}
              />
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('actions', {
      align: 'center',
      header: 'Actions',
      footer: (props) => props.column.id,
      width: 175,
      sortable: false,
      cell: (cell) => (
        <FuelActionbar
          actions={
            (permissions && permissions.has(Permission.OUSuperAdmin) && cell.row.original.segmentStatusInReplay === 'Live') ||
            cell.row.original.segmentStatusInReplay === 'Ingested'
              ? [
                  {
                    title: 'Delete',
                    className: 'action',
                    type: 'remove',
                    iconClass: 'icon',
                    iconName: '',
                    isDisabled: cell.row.original.segmentStatusInReplay === 'Live',
                    component: 'RemoveCircleIcon',
                  },
                ]
              : []
          }
          item={cell.row.original}
          onActionButtonClick={onTableAction}
          context={cell}
        />
      ),
    }),
  ];
  const onTableAction = async (event, type, item: Item, context: any) => {
    console.log('type', { type });
    if (type === 'remove' && item && item.id) {
      const replay: Replay = cloneDeep(replayRef.current);
      const isOkay = await vnConfirm({
        title: 'Delete Asset',
        message: 'Are you sure you want to delete this asset? When deleted, it will NOT be recoverable.',
      });
      if (isOkay && replayRef && replayRef.current) {
        // let replay: Replay = cloneDeep(replayRef.current);
        const clipsToDelete = replay.clips
          .filter((c) => c.id === item.id)
          .map((item) => {
            const liveEncoderService = item.data.externalServiceMetas.find((s) => s.serviceName === 'LiveEncoder');
            if (liveEncoderService) {
              return {
                name: item.title,
                id: liveEncoderService.serviceId,
              };
            }
          });

        replay.clips = clipsToDelete;
        // console.log(replay);
        try {
          if (item && item.segmentStatusInReplay === 'Live') {
            alert(`Could not delete growing clip from the pool, clipID:${item.id}, programId:${replay.programId}`, 'warning');
            return;
          }
          const deleted = await FuelPortalService.deleteReplayClip(replay);
          if (deleted) {
            alert('Deleted successfully!');
            await init();
          }
        } catch (e) {}
      }
    }
  };
  const onPaginationChange = () => {};
  const init = async () => {
    try {
      const response = await FuelPortalService.getNSLSegmentsInReplay(serviceId);
      if (response) {
        let list = enhanceFetchedAssets(response.clips, permissions, flags, false, [], [], ouData);
        // 5174 Adi- Forcing isDeleted to false so it should not be disabled in the fuel table, and users can delete it if they want.
        list = list.map((c) => {
          return { ...c, isDeleted: false };
        });
        const paginationObj = cloneDeep(initialPagination);
        paginationObj.totalItems = list.length;
        setPagination(paginationObj);
        setClips(list);
        replayRef.current = response;
      }
    } catch (e) {
      console.error('Replay pool not found', e);
      alert('Replay pool not found', 'error');
    } finally {
      setProgress(false);
    }
  };
  useEffect(() => {
    if (serviceId) {
      setProgress(true);
      init();
    }
  }, []);

  return (
    <>
      <VnModalBody>
        <FuelTable
          columnHelper={columnHelper}
          data={clips}
          defaultColumns={columns}
          paginationDetail={pagination}
          paginationChange={onPaginationChange}
          showRowSelection={false}
          loading={loading}
        />
      </VnModalBody>
      <VnModalFooter
        right={
          <Button autoFocus variant="contained" className="cancel-button" onClick={onClose}>
            Close
          </Button>
        }
      />
    </>
  );
};
export default SegmentsInReplayModal;
